import React from 'react';
import {useIntl} from 'react-intl';
import styled from 'styled-components';
import {FlexRow} from '@forecast-it/design-system';
import {WEEKDAYS, WEEKDAY_MESSAGE, WEEKDAY_SHORT_MESSAGE, type ScheduleWeekday} from './scheduleOptions';
import {SCHEDULING_MESSAGE} from './schedulingMessages';

// Round 32px toggles. The design-system ToggleGroup takes only a text label, so it cannot give each day
// its full name on hover and as its accessible name.
const DayButton = styled.button<{pressed: boolean}>`
	width: 32px;
	height: 32px;
	padding: 0;
	border-radius: 50%;
	cursor: pointer;
	font: ${({theme}) => theme.sys.font.detail['1']};
	font-weight: 500;
	border: 1px solid
		${({theme, pressed}) => (pressed ? theme.sys.color.background.primary['3'] : theme.sys.color.border.enabled)};
	background: ${({theme, pressed}) =>
		pressed ? theme.sys.color.background.primary['3'] : theme.sys.color.background.neutral['1']};
	color: ${({theme, pressed}) => (pressed ? theme.sys.color.text.content.ondark : theme.sys.color.text.content.main)};
	&:disabled {
		cursor: not-allowed;
		opacity: 0.5;
	}
`;

type WeekdayButtonsProps = {
	selected: ReadonlyArray<ScheduleWeekday>;
	disabled: boolean;
	onChange: (weekdays: ScheduleWeekday[]) => void;
};

export const WeekdayButtons = ({selected, disabled, onChange}: WeekdayButtonsProps): JSX.Element => {
	const {formatMessage} = useIntl();
	const toggle = (day: ScheduleWeekday) =>
		onChange(WEEKDAYS.filter(candidate => (candidate === day ? !selected.includes(day) : selected.includes(candidate))));
	return (
		<FlexRow gap={'xs'} role={'group'} aria-label={formatMessage({id: SCHEDULING_MESSAGE.DAYS_GROUP})}>
			{WEEKDAYS.map(day => {
				const name = formatMessage({id: WEEKDAY_MESSAGE(day)});
				const pressed = selected.includes(day);
				return (
					<DayButton
						key={day}
						type={'button'}
						pressed={pressed}
						aria-pressed={pressed}
						aria-label={name}
						title={name}
						disabled={disabled}
						onClick={() => toggle(day)}
						data-cy={`schedule-day-${day.toLowerCase()}`}
					>
						{formatMessage({id: WEEKDAY_SHORT_MESSAGE(day)})}
					</DayButton>
				);
			})}
		</FlexRow>
	);
};
